import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, Check, ExternalLink, Mail } from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useSites } from '@/hooks/useSites';
import { useEntitlements, type BillingInterval } from '@/hooks/useEntitlements';
import { supabase } from '@/integrations/supabase/client';
import { getStripeEnvironment, isStripeConfigured } from '@/lib/stripe';
import { toast } from '@/hooks/use-toast';
import { CONTACT } from '@/content/pricing';
import ModulesSection from './ModulesSection';
import PaymentTestModeBanner from './PaymentTestModeBanner';
import CheckoutDialog from './CheckoutDialog';
import { useSearchParams } from 'react-router-dom';

type Tier = 'standard' | 'professional';

const TIER_DEF: Record<Tier, {
  name: string;
  tagline: string;
  monthly: number;
  setup: number;
  highlights: string[];
}> = {
  standard: {
    name: 'Standard',
    tagline: 'For shelters serving up to 80 clients',
    monthly: 899,
    setup: 750,
    highlights: [
      'Up to 80 clients per facility',
      '10 admin + 20 staff seats per facility',
      'Core operations: census, intake, services, alerts',
      'Email support',
    ],
  },
  professional: {
    name: 'Professional',
    tagline: 'For shelters serving up to 200 clients',
    monthly: 1200,
    setup: 1200,
    highlights: [
      'Up to 200 clients per facility',
      '15 admin + 35 staff seats per facility',
      'Everything in Standard',
      'Priority support + onboarding training',
    ],
  },
};

const mailto = (subject: string) =>
  `mailto:${CONTACT.sales}?subject=${encodeURIComponent(subject)}`;

const BillingPage: React.FC = () => {
  const { organization } = useOrganization();
  const { sites } = useSites();
  const { baseSubscription, isPastDue, environment, isLoading } = useEntitlements();
  const [params, setParams] = useSearchParams();

  const [interval, setInterval] = useState<BillingInterval>('month');
  const [checkout, setCheckout] = useState<
    { kind: 'base'; tier: Tier; interval: BillingInterval } | null
  >(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const activeFacilities = sites.length;
  const currentTier = (baseSubscription?.tier ||
    ((organization as any)?.base_tier as Tier) ||
    'professional') as Tier;
  const setupAlreadyPaid = !!(organization as any)?.setup_fee_paid_at;

  // Handle return from embedded checkout
  useEffect(() => {
    const checkoutResult = params.get('billing');
    const sessionId = params.get('session_id');
    if (checkoutResult === 'success' && sessionId) {
      toast({
        title: 'Payment received',
        description:
          'Your subscription is being activated. You may need to refresh in a few seconds.',
      });
      params.delete('billing');
      params.delete('session_id');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const env = getStripeEnvironment();
      const { data, error } = await supabase.functions.invoke(
        'create-module-portal',
        { body: { environment: env } },
      );
      if (error) throw error;
      const payload = data as any;
      if (payload?.info === 'NO_BILLING_CUSTOMER') {
        toast({
          title: 'No billing yet',
          description: payload.message,
        });
        return;
      }
      const url = payload?.url;
      if (!url) throw new Error('No portal URL returned');
      window.open(url, '_blank');
    } catch (e: any) {
      toast({
        title: 'Could not open billing portal',
        description: e?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPortalBusy(false);
    }
  };

  const cancelBase = async () => {
    try {
      const env = getStripeEnvironment();
      const { error } = await supabase.functions.invoke(
        'cancel-module-subscription',
        { body: { kind: 'base', environment: env } },
      );
      if (error) throw error;
      toast({ title: 'Cancellation scheduled for period end.' });
    } catch (e: any) {
      toast({
        title: 'Could not cancel',
        description: e?.message,
        variant: 'destructive',
      });
    }
  };

  const resumeBase = async () => {
    try {
      const env = getStripeEnvironment();
      const { error } = await supabase.functions.invoke(
        'resume-module-subscription',
        { body: { kind: 'base', environment: env } },
      );
      if (error) throw error;
      toast({ title: 'Subscription resumed.' });
    } catch (e: any) {
      toast({
        title: 'Could not resume',
        description: e?.message,
        variant: 'destructive',
      });
    }
  };

  const totalRecurring = useMemo(() => {
    const t = TIER_DEF[currentTier];
    const unit = interval === 'year' ? t.monthly * 11 : t.monthly;
    return unit * Math.max(activeFacilities, 1);
  }, [currentTier, interval, activeFacilities]);

  return (
    <div>
      <PaymentTestModeBanner />
      <div className="max-w-6xl mx-auto py-8 px-4 space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="space-y-1">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              Plans &amp; billing
            </h1>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Per-facility licensing. Choose a base license; add modules as you
              need them. Your subscription quantity automatically updates when
              you add or archive a facility (prorated).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openPortal}
              disabled={portalBusy || !isStripeConfigured()}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {portalBusy ? 'Opening…' : 'Manage billing in Stripe'}
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a href={mailto('Billing question')}>
                <Mail className="h-4 w-4 mr-2" /> Contact us
              </a>
            </Button>
          </div>
        </div>

        {/* Past-due banner */}
        {isPastDue && (
          <Card className="border-red-300 bg-red-50 dark:bg-red-950/30 p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                Payment past due
              </p>
              <p className="text-xs text-red-700 dark:text-red-300">
                We could not charge your card. Stripe is retrying automatically;
                update your payment method in the billing portal to avoid losing
                access.
              </p>
            </div>
            <Button
              size="sm"
              variant="destructive"
              onClick={openPortal}
              disabled={portalBusy}
            >
              Update payment method
            </Button>
          </Card>
        )}

        {/* Current base license summary */}
        <Card className="bg-card rounded-xl p-6 border space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Current base license
              </p>
              <h2 className="text-2xl font-semibold text-foreground mt-1">
                {TIER_DEF[currentTier].name}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {organization?.name} · {activeFacilities}{' '}
                {activeFacilities === 1 ? 'facility' : 'facilities'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold tabular-nums text-foreground">
                ${totalRecurring.toLocaleString()}
                <span className="text-sm font-normal text-muted-foreground">
                  {' '}/ {interval === 'year' ? 'year' : 'mo'}
                </span>
              </p>
              {baseSubscription?.current_period_end && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Renews {new Date(baseSubscription.current_period_end).toLocaleDateString()}
                </p>
              )}
              {baseSubscription?.cancel_at_period_end && (
                <Badge variant="outline" className="mt-1">Cancels at period end</Badge>
              )}
            </div>
          </div>
          {baseSubscription?.stripe_subscription_id && (
            <div className="flex gap-2 pt-2">
              {baseSubscription.cancel_at_period_end ? (
                <Button size="sm" variant="outline" onClick={resumeBase}>
                  Resume subscription
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={cancelBase}>
                  Cancel at period end
                </Button>
              )}
            </div>
          )}
          {!baseSubscription?.stripe_subscription_id &&
            setupAlreadyPaid &&
            !isLoading && (
              <p className="text-xs text-muted-foreground">
                You are grandfathered onto Professional — no setup fee will ever be charged.
              </p>
            )}
        </Card>

        {/* Tier picker */}
        <section className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-foreground">
              {baseSubscription?.stripe_subscription_id ? 'Switch base license' : 'Choose a base license'}
            </h2>
            <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
              <button
                className={`px-3 py-1.5 ${interval === 'month' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground'}`}
                onClick={() => setInterval('month')}
              >
                Monthly
              </button>
              <button
                className={`px-3 py-1.5 ${interval === 'year' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground'}`}
                onClick={() => setInterval('year')}
              >
                Annual <span className="ml-1 text-emerald-600">12 for the price of 11</span>
              </button>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {(Object.keys(TIER_DEF) as Tier[]).map((tierId) => {
              const t = TIER_DEF[tierId];
              const isCurrent = tierId === currentTier && !!baseSubscription?.stripe_subscription_id;
              const price = interval === 'year' ? t.monthly * 11 : t.monthly;
              return (
                <Card
                  key={tierId}
                  className={`bg-card rounded-xl p-6 space-y-4 border ${isCurrent ? 'border-emerald-500/60' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-foreground">{t.name}</h3>
                    {isCurrent && (
                      <Badge variant="outline" className="text-xs border-emerald-500/60 text-emerald-700">
                        Current
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{t.tagline}</p>
                  <div>
                    <p className="text-3xl font-semibold text-foreground tabular-nums">
                      ${price.toLocaleString()}
                      <span className="text-sm font-normal text-muted-foreground">
                        {' '}/ facility / {interval === 'year' ? 'year' : 'month'}
                      </span>
                    </p>
                    {!setupAlreadyPaid && (
                      <p className="text-xs text-muted-foreground mt-2 border-t border-border pt-2">
                        <span className="font-medium text-foreground">
                          + ${t.setup.toLocaleString()} one-time setup fee
                        </span>{' '}
                        — charged with your first invoice only. Never charged
                        again (not on renewal, not on tier change).
                      </p>
                    )}
                    {setupAlreadyPaid && (
                      <p className="text-xs text-emerald-700 mt-2 border-t border-border pt-2">
                        ✓ Setup fee already paid (or grandfathered) — no setup
                        charge on tier change.
                      </p>
                    )}
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {t.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-foreground">
                        <Check className="h-3.5 w-3.5 text-emerald-600 mt-1 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    size="sm"
                    className="w-full"
                    variant={isCurrent ? 'secondary' : 'default'}
                    disabled={isCurrent || !isStripeConfigured()}
                    onClick={() => setCheckout({ kind: 'base', tier: tierId, interval })}
                  >
                    {isCurrent
                      ? 'Current plan'
                      : baseSubscription?.stripe_subscription_id
                        ? `Switch to ${t.name}`
                        : `Choose ${t.name}`}
                  </Button>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Add-on modules */}
        <ModulesSection />

        <Separator />

        <p className="text-xs text-muted-foreground">
          Environment: <span className="font-mono">{environment}</span>. Questions?{' '}
          <a className="underline" href={mailto('Billing question')}>{CONTACT.sales}</a>
        </p>
      </div>

      <CheckoutDialog
        open={!!checkout}
        onOpenChange={(v) => { if (!v) setCheckout(null); }}
        request={checkout}
        title="Complete your base license"
        description={
          checkout && !setupAlreadyPaid
            ? `First invoice includes your ${TIER_DEF[checkout.tier].name} subscription plus the one-time $${TIER_DEF[checkout.tier].setup.toLocaleString()} setup fee.`
            : 'Your base license starts immediately.'
        }
      />
    </div>
  );
};

export default BillingPage;
